import { Box, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useMemo } from 'react'
import { useResourceContext } from 'react-admin'

import AdvancedSelectInput from './AdvancedSelectInput'

export const toEnumTranslationValue = (id) => {
  if (id === null || id === undefined || id === '') return ''

  return String(id)
    .replace(/^__group_/, '')
    .toLowerCase()
}

const EnumChoiceLabel = ({
  icon: Icon = null,
  iconColor = 'text.secondary',
  iconSize = 16,
  label,
  labelVariant = 'body2',
  labelSx = {},
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      minWidth: 0,
      verticalAlign: 'middle',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
    }}
  >
    {Icon ? <Icon sx={{ fontSize: iconSize, color: iconColor, flexShrink: 0 }} /> : null}
    <Typography
      component="span"
      variant={labelVariant}
      sx={{ color: 'text.primary', whiteSpace: 'nowrap', ...labelSx }}
    >
      {label}
    </Typography>
  </Box>
)

const normalizeEnumIconChoices = (choices, resource, source, translate) =>
  choices.map((choice) => {
    const id = choice.id
    const value = toEnumTranslationValue(id)
    const translationKey = `resources.${resource}.enums.${source}.values.${value}`
    const name = translate(translationKey)

    return {
      ...choice,
      id,
      iconColor: choice.iconColor ?? 'text.secondary',
      iconSize: choice.iconSize ?? 16,
      name,
    }
  })

const EnumIconSelectInput = ({ source, choices = [], ...props }) => {
  const resource = useResourceContext()
  const translate = useTranslate()

  const normalizedChoices = useMemo(
    () => normalizeEnumIconChoices(choices, resource, source, translate),
    [choices, resource, source, translate],
  )

  return (
    <AdvancedSelectInput
      source={source}
      choices={normalizedChoices}
      optionText={(choice) => (
        <EnumChoiceLabel
          icon={choice.icon}
          iconColor={choice.iconColor}
          iconSize={choice.iconSize}
          label={choice.name}
        />
      )}
      {...props}
    />
  )
}

export const EnumField = ({
  source,
  choices = [],
  value,
  emptyLabel = null,
  labelVariant = 'body2',
  labelSx = {},
  icon = true,
  ...props
}) => {
  const resource = useResourceContext(props)
  const translate = useTranslate()

  const normalizedChoices = useMemo(
    () => normalizeEnumIconChoices(choices, resource, source, translate),
    [choices, resource, source, translate],
  )

  const choiceById = useMemo(
    () => Object.fromEntries(normalizedChoices.map((choice) => [String(choice.id), choice])),
    [normalizedChoices],
  )

  const choice = choiceById[String(value)]
  if (!choice)
    return emptyLabel ? (
      <Typography
        component="span"
        variant={labelVariant}
        sx={{ color: 'text.primary', whiteSpace: 'nowrap', ...labelSx }}
      >
        {emptyLabel}
      </Typography>
    ) : null

  return (
    <EnumChoiceLabel
      icon={icon ? choice.icon : null}
      iconColor={choice.iconColor}
      iconSize={choice.iconSize}
      label={choice.name}
      labelVariant={labelVariant}
      labelSx={labelSx}
    />
  )
}

export default EnumIconSelectInput
